import React, { useEffect, useState } from "react";
import "./HomeComponent.css";
import AgentCore from "../../components/agentCore/AgentCore";
import Header from "../../components/header/Header";
import portrait from "../../assets/images/my_image_g.png";
import { greeting } from "../../portfolio";

const systems = [
  {
    number: "01",
    title: "Stateful agents",
    copy:
      "Agents that retain useful context, make decisions across tools, and improve instead of resetting at every prompt.",
    tags: ["LangGraph", "Letta", "RAG"],
    accent: "cyan",
  },
  {
    number: "02",
    title: "Private intelligence",
    copy:
      "Local LLM systems with full tool use for teams whose data is too important to hand to someone else’s cloud.",
    tags: ["MCP", "Ollama", "On-prem"],
    accent: "violet",
  },
  {
    number: "03",
    title: "Systems in motion",
    copy:
      "Reliable data and AI pipelines with routing, retries, dead-letter queues, and clear ownership from input to outcome.",
    tags: ["Kafka", "Azure", "FastAPI"],
    accent: "orange",
  },
];

const caseFiles = [
  {
    index: "CASE_01",
    label: "LOCAL INTELLIGENCE",
    title: "MCP × Ollama",
    detail:
      "A modular MCP bridge that gives locally hosted models Claude-like tools while keeping sensitive work on-prem.",
    outcome: "26 GitHub stars",
    stack: ["Python", "MCP", "Ollama"],
    link: "https://github.com/Sethuram2003/MCP-ollama_server",
    kind: "local",
  },
  {
    index: "CASE_02",
    label: "MEMORY THAT COMPOUNDS",
    title: "AdaptiMind",
    detail:
      "A context-aware agent that learns from interaction history and adjusts its own memory instead of treating every exchange as new.",
    outcome: "90% retrieval lift",
    stack: ["Llama 3.1", "Letta", "Tools"],
    link:
      "https://github.com/Sethuram2003/AdaptiMind-Context-Aware-AI-Agent-with-Dynamic-Memory",
    kind: "memory",
  },
  {
    index: "CASE_03",
    label: "VOICE → ACTION",
    title: "Realtime voice agent",
    detail:
      "A speaker-aware system that listens, transcribes, reasons, and responds as a live conversation unfolds.",
    outcome: "Realtime interaction",
    stack: ["Voice", "LangChain", "FastAPI"],
    link: "https://github.com/Sethuram2003/LangchainVoiceAgent",
    kind: "voice",
  },
];

function Arrow({ diagonal = false }) {
  return (
    <span className={diagonal ? "arrow arrow-diagonal" : "arrow"}>
      {diagonal ? "↗" : "→"}
    </span>
  );
}

export default function Home(props) {
  const [progress, setProgress] = useState(0);
  const isDark = props.isDark !== false;

  useEffect(() => {
    const updateProgress = () => {
      const scrollable =
        document.documentElement.scrollHeight - window.innerHeight;
      setProgress(
        scrollable > 0 ? Math.min(1, window.pageYOffset / scrollable) : 0
      );
    };
    updateProgress();
    window.addEventListener("scroll", updateProgress, { passive: true });
    window.addEventListener("resize", updateProgress);
    return () => {
      window.removeEventListener("scroll", updateProgress);
      window.removeEventListener("resize", updateProgress);
    };
  }, []);

  return (
    <div
      className={`observatory ${
        isDark ? "observatory-dark" : "observatory-light"
      }`}
    >
      <div className="observatory-grain" aria-hidden="true"></div>
      <div className="observatory-grid" aria-hidden="true"></div>
      <div className="observatory-progress" aria-hidden="true">
        <span style={{ transform: `scaleX(${progress})` }}></span>
      </div>

      <Header theme={props.theme} onToggle={props.onToggle} isDark={isDark} />

      <main>
        <section className="obs-hero" id="top">
          <div className="hero-utility hero-utility-left">
            <span className="utility-dot"></span>
            <span>OBSERVATORY / 2026</span>
          </div>
          <div className="hero-utility hero-utility-right">
            <span>NEW YORK / REMOTE</span>
            <span className="utility-separator">•</span>
            <span>OPEN TO WORK</span>
          </div>

          <div className="hero-copy">
            <p className="eyebrow">
              <span></span> Sethuram Gautham · AI/ML engineer
            </p>
            <h1>
              I engineer
              <br />
              intelligence that
              <br />
              <em>ships.</em>
            </h1>
            <p className="hero-lede">
              Stateful agents. Private LLM infrastructure. Production-grade
              systems that earn their place in the real world.
            </p>
            <div className="hero-actions">
              <a className="button-primary" href="#case-files">
                View selected work <Arrow />
              </a>
              <a
                className="button-secondary"
                href={greeting.resumeLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                View résumé <Arrow diagonal />
              </a>
            </div>
          </div>

          <div className="hero-core-stage">
            <div className="core-frame-corner corner-tl"></div>
            <div className="core-frame-corner corner-tr"></div>
            <div className="core-frame-corner corner-bl"></div>
            <div className="core-frame-corner corner-br"></div>
            <div className="core-frame-meta core-meta-top">
              <span>AGENT_CORE</span>
              <span>LIVE</span>
            </div>
            <AgentCore isDark={isDark} />
            <div className="core-label core-label-memory">
              <b>MEMORY</b>
              <span>STATEFUL</span>
            </div>
            <div className="core-label core-label-tools">
              <b>TOOLS</b>
              <span>ORCHESTRATED</span>
            </div>
            <div className="core-label core-label-private">
              <b>PRIVACY</b>
              <span>LOCAL FIRST</span>
            </div>
            <div className="core-frame-meta core-meta-bottom">
              <span>NEURAL INTERFACE</span>
              <span>v1.0</span>
            </div>
          </div>

          <div className="hero-portrait-card">
            <div className="portrait-glow"></div>
            <img
              src={portrait}
              alt="Ghibli-style portrait of Sethuram Gautham"
            />
            <div className="portrait-card-detail">
              <span>HUMAN IN THE LOOP</span>
              <b>SG / 03</b>
            </div>
          </div>

          <a
            className="hero-scroll"
            href="#systems"
            aria-label="Scroll to systems"
          >
            <span>SCROLL TO EXPLORE</span>
            <i></i>
          </a>
          <div className="hero-status-bar">
            <span>
              <i className="status-pulse"></i> SYSTEM STATUS / AVAILABLE
            </span>
            <span>FOCUS: AGENTS · LOCAL AI · DATA SYSTEMS</span>
          </div>
        </section>

        <section className="ticker" aria-label="Areas of expertise">
          <div className="ticker-track">
            <span>AGENT SYSTEMS</span>
            <i>✦</i>
            <span>PRIVATE INTELLIGENCE</span>
            <i>✦</i>
            <span>HUMAN-CENTERED AI</span>
            <i>✦</i>
            <span>DATA IN MOTION</span>
            <i>✦</i>
            <span>AGENT SYSTEMS</span>
            <i>✦</i>
            <span>PRIVATE INTELLIGENCE</span>
            <i>✦</i>
            <span>HUMAN-CENTERED AI</span>
            <i>✦</i>
            <span>DATA IN MOTION</span>
            <i>✦</i>
          </div>
        </section>

        <section className="systems-section section-shell" id="systems">
          <div className="section-heading section-heading-systems">
            <div>
              <p className="eyebrow">
                <span></span> Operating principles
              </p>
              <h2>
                Complexity is only
                <br />
                <em>useful when it disappears.</em>
              </h2>
            </div>
            <p>
              I build the layer beneath the interface: the decisions, memory,
              routing, data, and infrastructure that let AI do useful work
              without becoming a liability.
            </p>
          </div>

          <div
            className="system-architecture"
            aria-label="AI system architecture diagram"
          >
            <div className="architecture-legend">
              <span>
                <i className="legend-cyan"></i> signal
              </span>
              <span>
                <i className="legend-violet"></i> context
              </span>
              <span>
                <i className="legend-orange"></i> action
              </span>
            </div>
            <div className="architecture-canvas">
              <div className="architecture-lines" aria-hidden="true">
                <i></i>
                <i></i>
                <i></i>
                <i></i>
                <i></i>
                <i></i>
              </div>
              <div className="architecture-node architecture-node-core">
                <span>ROUTER</span>
                <b>DECIDE</b>
                <i className="node-beacon"></i>
              </div>
              <div className="architecture-node architecture-node-memory">
                <span>MEMORY</span>
                <b>RECALL</b>
              </div>
              <div className="architecture-node architecture-node-tools">
                <span>TOOLS</span>
                <b>ACT</b>
              </div>
              <div className="architecture-node architecture-node-model">
                <span>MODELS</span>
                <b>REASON</b>
              </div>
              <div className="architecture-node architecture-node-guard">
                <span>GUARDRAILS</span>
                <b>VERIFY</b>
              </div>
              <div className="architecture-stream stream-one"></div>
              <div className="architecture-stream stream-two"></div>
            </div>
            <p className="architecture-caption">
              A system is only as intelligent as its handoffs.
            </p>
          </div>

          <div className="system-cards">
            {systems.map((system) => (
              <article
                className={`system-card system-card-${system.accent}`}
                key={system.number}
              >
                <span className="system-number">{system.number}</span>
                <div className="system-card-art" aria-hidden="true">
                  <i></i>
                  <i></i>
                  <i></i>
                </div>
                <h3>{system.title}</h3>
                <p>{system.copy}</p>
                <div className="system-tags">
                  {system.tags.map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="case-section section-shell" id="case-files">
          <div className="section-heading case-heading">
            <div>
              <p className="eyebrow">
                <span></span> Selected transmissions
              </p>
              <h2>
                Work worth
                <br />
                <em>opening up.</em>
              </h2>
            </div>
            <a
              className="text-action"
              href="https://github.com/Sethuram2003"
              target="_blank"
              rel="noopener noreferrer"
            >
              All repositories <Arrow diagonal />
            </a>
          </div>
          <div className="case-grid">
            {caseFiles.map((file) => (
              <article
                className={`case-card case-card-${file.kind}`}
                key={file.index}
              >
                <div className="case-card-topline">
                  <span>{file.index}</span>
                  <span>{file.label}</span>
                </div>
                <div className="case-visual" aria-hidden="true">
                  {file.kind === "local" && (
                    <div className="local-terminal">
                      <span>$ init_local_intelligence</span>
                      <i></i>
                      <span>models: secure</span>
                      <span>tools: connected</span>
                      <b>01</b>
                    </div>
                  )}
                  {file.kind === "memory" && (
                    <div className="memory-visual">
                      <i></i>
                      <i></i>
                      <i></i>
                      <i></i>
                      <i></i>
                      <span>
                        context
                        <br />
                        persists
                      </span>
                    </div>
                  )}
                  {file.kind === "voice" && (
                    <div className="voice-visual">
                      <i></i>
                      <i></i>
                      <i></i>
                      <i></i>
                      <i></i>
                      <i></i>
                      <span>LIVE</span>
                    </div>
                  )}
                </div>
                <div className="case-card-copy">
                  <h3>{file.title}</h3>
                  <p>{file.detail}</p>
                </div>
                <div className="case-card-footer">
                  <div>
                    <span className="case-outcome">{file.outcome}</span>
                    <div className="case-stack">
                      {file.stack.map((tag) => (
                        <span key={tag}>{tag}</span>
                      ))}
                    </div>
                  </div>
                  <a
                    href={file.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${file.title} on GitHub`}
                  >
                    <Arrow diagonal />
                  </a>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="evidence-band">
          <div className="evidence-item">
            <b>08</b>
            <span>
              months building
              <br />
              production AI
            </span>
          </div>
          <div className="evidence-item">
            <b>
              97.45<span>%</span>
            </b>
            <span>
              Braille recognition
              <br />
              accuracy
            </span>
          </div>
          <div className="evidence-item">
            <b>01</b>
            <span>
              patented accessibility
              <br />
              device
            </span>
          </div>
          <div className="evidence-item">
            <b>IEEE</b>
            <span>
              published research
              <br />
              at ICNWC 2024
            </span>
          </div>
        </section>

        <section className="signal-section section-shell" id="signal-log">
          <div className="section-heading signal-heading">
            <div>
              <p className="eyebrow">
                <span></span> Signal log
              </p>
              <h2>
                Evidence, not
                <br />
                <em>adjectives.</em>
              </h2>
            </div>
            <p>
              From edge hardware to enterprise agent systems, the throughline is
              simple: make hard technology useful to real people.
            </p>
          </div>
          <div className="signal-layout">
            <div className="signal-timeline">
              <article className="timeline-item timeline-current">
                <span className="timeline-date">2025 — 2026</span>
                <div className="timeline-marker">
                  <i></i>
                </div>
                <div className="timeline-content">
                  <p>HGS DIGITAL / NEW YORK</p>
                  <h3>Associate Software Engineer</h3>
                  <span>
                    Voice agents · multi-agent routing · Azure Functions ·
                    privacy-first local LLM demos
                  </span>
                </div>
              </article>
              <article className="timeline-item">
                <span className="timeline-date">2024 — 2026</span>
                <div className="timeline-marker">
                  <i></i>
                </div>
                <div className="timeline-content">
                  <p>STONY BROOK UNIVERSITY</p>
                  <h3>M.S. Data Science</h3>
                  <span>
                    Built AdaptiMind, a memory-aware AI agent with a 90%
                    retrieval improvement.
                  </span>
                </div>
              </article>
              <article className="timeline-item">
                <span className="timeline-date">2020 — 2024</span>
                <div className="timeline-marker">
                  <i></i>
                </div>
                <div className="timeline-content">
                  <p>SSN COLLEGE OF ENGINEERING</p>
                  <h3>B.E. Electrical & Electronics</h3>
                  <span>
                    Built a patented Tamil Braille reader and published IoT
                    monitoring research.
                  </span>
                </div>
              </article>
            </div>
            <aside className="patent-panel">
              <div className="patent-serial">RESEARCH / 02</div>
              <div className="braille-grid" aria-hidden="true">
                {[...Array(18)].map((_, index) => (
                  <i
                    className={
                      [1, 2, 4, 6, 7, 10, 12, 13, 16].includes(index)
                        ? "is-raised"
                        : ""
                    }
                    key={index}
                  ></i>
                ))}
              </div>
              <p className="eyebrow">
                <span></span> Patent holder
              </p>
              <h3>
                Tamil Braille
                <br />
                reader
              </h3>
              <p className="patent-copy">
                A handheld Raspberry Pi and CNN system that reads Tamil Braille
                aloud — designed for access, not just accuracy.
              </p>
              <a
                className="text-action"
                href="https://github.com/Sethuram2003"
                target="_blank"
                rel="noopener noreferrer"
              >
                View the record <Arrow diagonal />
              </a>
            </aside>
          </div>
        </section>

        <section className="connect-section" id="connect">
          <div
            className="connect-orbit connect-orbit-one"
            aria-hidden="true"
          ></div>
          <div
            className="connect-orbit connect-orbit-two"
            aria-hidden="true"
          ></div>
          <div className="connect-inner section-shell">
            <div className="connect-copy">
              <p className="eyebrow">
                <span></span> Connection established
              </p>
              <h2>
                Give your next
                <br />
                difficult system
                <br />
                <em>a brain.</em>
              </h2>
              <p>
                Looking for an AI/ML engineer who can move from architecture to
                an actually working system? I’m ready to talk.
              </p>
            </div>
            <div className="command-console">
              <div className="console-top">
                <span>
                  <i></i>
                  <i></i>
                  <i></i>
                </span>
                <b>outreach.sh</b>
                <span>•••</span>
              </div>
              <div className="console-body">
                <p>
                  <span>$</span> availability --check
                </p>
                <p className="console-success">
                  ✓ accepting full-time AI/ML roles
                </p>
                <p>
                  <span>$</span> contact --preferred
                </p>
                <a href="mailto:gauthamr2003@gmail.com">
                  gauthamr2003@gmail.com <Arrow diagonal />
                </a>
                <p>
                  <span>$</span> network --open
                </p>
                <div className="console-links">
                  <a
                    href="https://www.linkedin.com/in/sethuramgautham/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    LinkedIn <Arrow diagonal />
                  </a>
                  <a
                    href="https://github.com/Sethuram2003"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    GitHub <Arrow diagonal />
                  </a>
                </div>
                <div className="console-cursor">
                  <span>$</span>
                  <i></i>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="obs-footer section-shell">
        <a className="obs-footer-brand" href="#top">
          <span className="obs-footer-mark">SG</span>
          <span className="obs-footer-copy">
            PRIVATE INTELLIGENCE
            <br />
            OBSERVATORY
          </span>
        </a>
        <p>Designed around systems that remember, reason, and ship.</p>
        <span>© {new Date().getFullYear()} SETHURAM GAUTHAM</span>
      </footer>
    </div>
  );
}
